import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Lock, ArrowRight } from "lucide-react";

export default function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/community";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) navigate(from, { replace: true });
    else setErr(res.error);
  };

  return (
    <div data-testid="page-signin" className="py-20 lg:py-28">
      <div className="max-w-md mx-auto px-6">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
          <span className="w-6 h-px bg-terracotta" />
          Welcome back
        </span>
        <h1 className="font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl leading-[1.05] tracking-tight">
          Sign in to the <em className="text-terracotta">community</em>.
        </h1>
        <p className="mt-4 text-base text-charcoal-light dark:text-bone/65 leading-relaxed">
          A quiet space for people who get it. Your first name is the only thing others will see.
        </p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="signin-form">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.22em] text-charcoal-light dark:text-bone/60">Email</span>
            <div className="mt-2 relative">
              <Mail size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-light dark:text-bone/50" />
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signin-email"
                className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-bone-200 dark:border-charcoal-light/30 bg-bone dark:bg-charcoal-light/10 text-charcoal dark:text-bone outline-none focus:border-terracotta transition"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.22em] text-charcoal-light dark:text-bone/60">Password</span>
            <div className="mt-2 relative">
              <Lock size={16} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal-light dark:text-bone/50" />
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="signin-password"
                className="w-full pl-11 pr-4 py-3.5 rounded-2xl border border-bone-200 dark:border-charcoal-light/30 bg-bone dark:bg-charcoal-light/10 text-charcoal dark:text-bone outline-none focus:border-terracotta transition"
              />
            </div>
          </label>

          {err && (
            <p data-testid="signin-error" className="text-sm text-terracotta">
              {err}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="signin-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-terracotta text-bone text-sm font-medium tracking-wide hover:bg-terracotta-hover transition disabled:opacity-60"
          >
            {busy ? "Signing in…" : "Sign in"}
            {!busy && <ArrowRight size={16} strokeWidth={1.5} />}
          </button>
        </form>

        <p className="mt-8 text-sm text-charcoal-light dark:text-bone/65 text-center">
          New here?{" "}
          <Link to="/signup" data-testid="signin-to-signup" className="text-terracotta hover:underline">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
